import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProvidersReviewsSection = ({ onAddToCompare }) => {
  const [addedProviders, setAddedProviders] = useState({});

  const handleAddToCompare = (provider, index) => {
    if (onAddToCompare) {
      const productData = {
        id: `provider-heatpump-${index}`,
        type: 'Provider',
        brandName: provider?.name,
        name: provider?.specialties?.join(', '),
        image: 'https://ui-avatars.com/api/?name=' + encodeURIComponent(provider?.name) + '&background=3b82f6&color=fff&size=200',
        imageAlt: `${provider?.name} logo`,
        price: 'Op aanvraag',
        rating: provider?.rating,
        reviewCount: provider?.reviewCount,
        specs: [
          { icon: 'Award', label: 'Specialiteiten', value: provider?.specialties?.join(', ') },
          { icon: 'MapPin', label: 'Locatie', value: provider?.location },
          { icon: 'Clock', label: 'Reactietijd', value: provider?.responseTime }
        ],
        specialization: provider?.specialties?.join(', '),
        certifications: provider?.certified ? ['Gecertificeerd'] : [],
        warranty: 'Standaard garantie',
        reviews: []
      };
      onAddToCompare(productData);
      setAddedProviders(prev => ({ ...prev, [index]: true }));
      setTimeout(() => {
        setAddedProviders(prev => ({ ...prev, [index]: false }));
      }, 2000);
    }
  };

  const providers = [
    {
      name: 'WarmtepompExpert Nederland',
      rating: 4.9,
      reviewCount: 428,
      specialties: ['All-electric', 'Hybride', 'Advies'],
      location: 'Landelijk',
      certified: true,
      responseTime: '< 12 uur'
    },
    {
      name: 'DuurzaamVerwarmen.nl',
      rating: 4.8,
      reviewCount: 356,
      specialties: ['Bodemgebonden', 'All-electric'],
      location: 'Randstad',
      certified: true,
      responseTime: '< 24 uur'
    },
    {
      name: 'EcoHeat Installaties',
      rating: 4.7,
      reviewCount: 289,
      specialties: ['Hybride', 'Service'],
      location: 'Zuid-Nederland',
      certified: true,
      responseTime: '< 24 uur'
    }
  ];

  const reviews = [
    {
      name: 'Sophie van Dam',
      location: 'Amersfoort',
      rating: 5,
      date: 'Januari 2026',
      project: 'All-electric warmtepomp',
      review: 'Volledig gasvrij en de woning is heerlijk warm. De investering was fors maar met de subsidie en de maandelijkse besparing ben ik zeer tevreden. Professionele installatie.',
      savings: '€1.450/jaar',
      image: 'https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      name: 'Mark Hendriks',
      location: 'Den Haag',
      rating: 5,
      date: 'December 2025',
      project: 'Hybride warmtepomp',
      review: 'Perfecte oplossing voor onze jaren 30 woning. De warmtepomp doet het meeste werk en de cv-ketel springt alleen bij bij extreem koud weer. Gasverbruik met 60% gedaald.',
      savings: '€780/jaar',
      image: 'https://images.pexels.com/photos/8853461/pexels-photo-8853461.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      name: 'Linda Bakker',
      location: 'Eindhoven',
      rating: 4,
      date: 'November 2025',
      project: 'Bodemgebonden warmtepomp',
      review: 'Hoog rendement en zeer stil systeem. De aanleg was intensief maar het resultaat is fantastisch. Woning is het hele jaar door comfortabel en de energiekosten zijn minimaal.',
      savings: '€1.850/jaar',
      image: 'https://images.pexels.com/photos/5691622/pexels-photo-5691622.jpeg?auto=compress&cs=tinysrgb&w=400'
    }
  ];

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name={star <= rating ? 'Star' : 'Star'}
            size={16}
            className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Aanbieders & ervaringen
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Gecertificeerde warmtepomp installateurs met bewezen expertise en tevreden klanten.
          </p>
        </motion.div>

        {/* Providers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {providers?.map((provider, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-card border border-border rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-3 transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    {provider?.name}
                  </h3>
                  <div className="flex items-center space-x-2 mb-2">
                    {renderStars(Math?.floor(provider?.rating))}
                    <span className="text-sm font-semibold text-foreground">{provider?.rating}</span>
                    <span className="text-sm text-muted-foreground">({provider?.reviewCount})</span>
                  </div>
                </div>
                {provider?.certified && (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon name="BadgeCheck" size={20} className="text-green-600" />
                  </div>
                )}
              </div>

              {/* Specialties */}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Specialisaties</p>
                <div className="flex flex-wrap gap-2">
                  {provider?.specialties?.map((specialty, idx) => (
                    <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="MapPin" size={14} />
                  <span>{provider?.location}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Reactie: {provider?.responseTime}</span>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-wrap gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAddToCompare(provider, index)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    addedProviders?.[index]
                      ? 'bg-success text-white' :'bg-gray-100 hover:bg-gray-200 text-foreground'
                  }`}
                >
                  <Icon name={addedProviders?.[index] ? 'Check' : 'GitCompare'} size={18} />
                  <span className="text-sm font-medium">
                    {addedProviders?.[index] ? 'Toegevoegd' : 'Vergelijken'}
                  </span>
                </motion.button>
                <Link to="/quote-request-form">
                  <Button size="lg" className="w-full sm:w-auto">
                    Offerte aanvragen
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Customer Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-heading font-bold text-foreground mb-8 text-center">
            Ervaringen van woningeigenaren
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {reviews?.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-card border border-border rounded-xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-all"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={review?.image}
                    alt={`${review?.project} installatie van ${review?.name}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {review?.savings}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-foreground">{review?.name}</p>
                      <p className="text-sm text-muted-foreground">{review?.location}</p>
                    </div>
                    {renderStars(review?.rating)}
                  </div>

                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {review?.project}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground">{review?.date}</span>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-4">
                    {review?.review}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
              Klaar voor een warmtepomp?
            </h3>
            <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
              Ontvang binnen 24 uur vrijblijvende offertes van gecertificeerde warmtepomp installateurs in uw regio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote-request-form">
                <Button size="lg" variant="secondary" iconName="FileText" iconPosition="left">
                  Offerte aanvragen
                </Button>
              </Link>
              <Button size="lg" variant="outline" iconName="Phone" iconPosition="left" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                Bel voor advies
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProvidersReviewsSection;